import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { graphql } from '../../__generated__/gql';
import { usePortalPermissions } from '../../Common/hooks/usePortalPermissions';
import { useEffect } from 'react';
import { pusherSubscribeObject } from '../../Common/utils/pusher';
import { titleForMessageThread } from '../utils/messageUtils';
import {
  getLocalNotificationsPermission,
  showLocalNotification,
} from '../../Common/utils/localNotifications';
import { useRouting } from '../../Common/hooks/useRouting';

export const unreadMessageCountQuery = graphql(`
  query unreadMessagesCount {
    unreadMessagesCount
  }
`);

// Fetches thread metadata + the single most recent message for notification previews.
// Uses getMessagesPaged(limit:1) — the first item in messages[] is the latest message.
const threadNotificationQuery = gql`
  query threadNotification($limit: Int, $threadId: ID) {
    getMessagesPaged(limit: $limit) {
      id
      __typename
      unreadDoctor
      unreadCustomer
      channel
      assignedUser
      identityChannel
      brand
      open
      customer {
        id
        given
        mobile
        surname
        brand
        name
        __typename
      }
      messages {
        __typename
        id
        sent
        html
        title
        files
        senderID
        message
        final
        photo
        type
        options
      }
    }
  }
`;

export const useInboxMessagesCount = () => {
  const { hasPermission, brands } = usePortalPermissions();

  const hasMessagingPermissions = hasPermission('messaging');

  const { data, refetch } = useQuery(unreadMessageCountQuery, {
    skip: !hasMessagingPermissions,
  });

  const { navigate } = useRouting();

  const [getThreadQuery] = useLazyQuery(threadNotificationQuery);

  const newMessage = async (threadId: string) => {
    const { data } = await getThreadQuery({ variables: { limit: 1 } });

    const messageThread = data?.getMessagesPaged as any;
    const title = titleForMessageThread(messageThread);
    // Most recent message is messages[0] since getMessagesPaged returns newest-first reversed.
    const subtitle = messageThread?.messages?.[messageThread?.messages?.length - 1]?.message ?? '';

    showLocalNotification(title, subtitle, () => {
      navigate(`/inbox/${threadId}`);
    });
  };

  useEffect(() => {
    getLocalNotificationsPermission();

    const subscribedBrands: (() => void)[] = [];

    if (hasMessagingPermissions && brands?.length > 0) {
      brands.forEach((brand) => {
        const { unsubscribe } = pusherSubscribeObject(`inbox-${brand}`, 'thread', (data) => {
          refetch().catch(null);

          if (data?.threadId) {
            newMessage(data?.threadId).catch(null);
          }
        });

        subscribedBrands.push(unsubscribe);
      });
    }

    return () => {
      if (subscribedBrands) {
        subscribedBrands.forEach((unsubscribe) => unsubscribe());
      }
    };
  }, [brands, hasMessagingPermissions]);

  return {
    count: data?.unreadMessagesCount ?? 0,
  };
};
